import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Phone, Star, BadgeCheck, Clock, CalendarCheck, ShieldCheck } from "lucide-react";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import Reviews from "@/components/landing/Reviews";
import Faq from "@/components/landing/Faq";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_EMBED = process.env.REACT_APP_BOOKING_EMBED_URL;

const fuelLabel = { electric: "Electric", gas: "Gas" };
const systemLabel = { storage: "Storage", continuous: "Continuous Flow", instantaneous: "Instantaneous" };

const sizeChip = (u) => {
  if (u.system === "continuous") return `${u.size_class}L/MIN`;
  if (u.system === "instantaneous") {
    return /^e/i.test(String(u.size_class)) ? `${String(u.size_class).replace(/^e/i, "")}KW` : `${u.size_class}L/MIN`;
  }
  return `${u.size_class}L`;
};

export default function UnitPage() {
  const { slug } = useParams();
  const [unit, setUnit] = useState(null);
  const [allUnits, setAllUnits] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setUnit(null);
    setNotFound(false);
    axios.get(`${API}/units/${slug}`)
      .then((r) => setUnit(r.data.unit))
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    axios.get(`${API}/units`).then((r) => setAllUnits(r.data.units || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (unit) {
      document.title = `${unit.brand} ${unit.model} ${fuelLabel[unit.fuel] || ""} Hot Water Heater — $${unit.price.toLocaleString()} Installed | Hot Water Systems Perth`;
      const setMeta = (attr, key, content) => {
        let el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(attr, key);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };
      const origin = process.env.REACT_APP_CANONICAL_BASE;
      setMeta("property", "og:title", document.title);
      setMeta("property", "og:type", "product");
      setMeta("property", "og:url", `${origin}/units/${unit.slug}`);
      setMeta("property", "og:image", `${origin}/unit-photos/${unit.id.toLowerCase()}-landscape.jpg`);
      setMeta("name", "twitter:image", `${origin}/unit-photos/${unit.id.toLowerCase()}-landscape.jpg`);
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute("href", `${origin}/units/${unit.slug}`);
    }
  }, [unit]);

  const scrollToBook = () => document.getElementById("unit-book")?.scrollIntoView({ behavior: "smooth" });

  if (notFound) {
    return (
      <div className="min-h-screen bg-tpeco-blue flex flex-col items-center justify-center text-white gap-4 px-4">
        <h1 className="font-display uppercase text-4xl text-center">UNIT NOT FOUND</h1>
        <Link to="/" className="btn-brutal" data-testid="unit-notfound-home">SEE ALL UNITS &amp; PRICES</Link>
      </div>
    );
  }
  if (!unit) return <div className="min-h-screen bg-white" data-testid="unit-page-loading" />;

  const title = `${unit.brand} ${unit.model} ${fuelLabel[unit.fuel] || ""} Hot Water Heater`;
  const others = allUnits.filter((u) =>
    u.slug !== unit.slug && u.available &&
    u.fuel === unit.fuel && u.system === unit.system && u.size_class === unit.size_class
  );

  return (
    <div className="min-h-screen bg-white text-tpeco-ink" data-testid="unit-page">
      <Nav onBook={scrollToBook} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {/* photo — plumber + unit fills the container */}
          <div className="card-brutal bg-white aspect-square relative lg:self-start" data-testid="unit-photo-card">
            <img
              src={`/unit-photos/${unit.id.toLowerCase()}-square.jpg`}
              alt={`${title} installed by our Perth plumber`}
              className="absolute w-[126%] max-w-none left-[3.4%] -top-[14.4%]"
              data-testid="unit-photo"
            />
          </div>

          {/* title, specs, price, ctas */}
          <div>
            <span className="chip-brutal bg-tpeco-blue border-tpeco-blue text-white">ITEM {unit.id} · SUPPLIED &amp; INSTALLED</span>
            <h1 className="mt-3 font-display uppercase text-4xl sm:text-5xl leading-[0.95]" data-testid="unit-title">{title}</h1>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="unit-specs">
              <span className="chip-brutal bg-white">{(fuelLabel[unit.fuel] || unit.fuel).toUpperCase()}</span>
              <span className="chip-brutal bg-white">{(systemLabel[unit.system] || unit.system).toUpperCase()}</span>
              <span className="chip-brutal bg-white">{sizeChip(unit)}</span>
              <span className="chip-brutal bg-white">SUITS {unit.people.toUpperCase()}</span>
            </div>
            <p className="mt-4 font-semibold text-neutral-700">{unit.blurb}</p>
            <p className="mt-2 text-sm font-bold text-neutral-600">Warranty: {unit.warranty}</p>

            <div className="mt-5 rounded-3xl border-[3px] border-tpeco-ink bg-tpeco-blue text-white p-6 shadow-[6px_6px_0_0_#0A0A0A]" data-testid="unit-price-box">
              <span className="chip-brutal bg-white border-white text-tpeco-blue !text-xs sm:!text-sm">FULLY INSTALLED PRICE — NO SURPRISES</span>
              <p className="font-display text-6xl sm:text-7xl leading-none mt-3" data-testid="unit-price">
                ${unit.price.toLocaleString()} <span className="text-2xl sm:text-3xl text-white/90">INSTALLED</span>
              </p>
              <p className="mt-2 text-sm font-semibold text-white/85">Unit + labour + materials included. Like-for-like swap, same or next day across Perth.</p>
              <div className="mt-4 rounded-2xl border-2 border-dashed border-white bg-tpeco-pink px-4 py-3 flex items-center gap-3" data-testid="unit-price-beat">
                <ShieldCheck className="w-8 h-8 shrink-0" strokeWidth={2.5} />
                <p className="font-display uppercase text-lg sm:text-xl leading-tight">WE'LL BEAT ANY COMPETITOR'S WRITTEN QUOTE. GUARANTEED.</p>
              </div>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <a href="tel:0475776555" className="btn-brutal justify-center !text-xl" data-testid="unit-call-button">
                <Phone className="w-5 h-5" strokeWidth={2.5} /> CALL 0475 776 555
              </a>
              <button onClick={scrollToBook} className="btn-brutal-dark justify-center !text-xl" data-testid="unit-book-scroll-button">
                <CalendarCheck className="w-5 h-5" strokeWidth={2.5} /> BOOK A LIVE SLOT ↓
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs font-bold uppercase tracking-wide text-neutral-600">
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-tpeco-pink" /> SAME / NEXT DAY</span>
              <span className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-tpeco-pink" /> LICENSED &amp; INSURED</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-tpeco-pink" /> 100+ 5 STAR REVIEWS</span>
            </div>
          </div>
        </div>

        {/* booking portal under the hero image + other units next to it */}
        <div id="unit-book" className="mt-10 grid lg:grid-cols-2 gap-8 lg:gap-10 items-start scroll-mt-24">
          <div className="rounded-[2rem] border-[3px] border-tpeco-ink bg-white shadow-[0_10px_0_0_#0A0A0A] overflow-hidden" data-testid="unit-booking-portal">
            <div className="flex items-center justify-between gap-2 bg-tpeco-pink text-white px-4 py-3">
              <span className="font-display uppercase text-xl flex items-center gap-2"><CalendarCheck className="w-5 h-5" /> LIVE AVAILABILITY</span>
              <span className="chip-brutal bg-tpeco-blue border-white text-white !text-xs">BOOK A REAL SLOT ↓</span>
            </div>
            <iframe
              src={`${BOOKING_EMBED}?unit_id=${unit.id}`}
              title="Live booking portal — The Plumbing & Electrical Co."
              className="w-full h-[640px] bg-white"
              loading="lazy"
              data-testid="unit-booking-iframe"
            />
            <div className="border-t-2 border-tpeco-ink/10 px-4 py-3 text-center text-xs font-bold uppercase tracking-widest text-neutral-500">
              Portal not loading? <a href="tel:0475776555" className="text-tpeco-blue underline underline-offset-2" data-testid="unit-iframe-fallback-call">Call 0475 776 555</a> and we'll book you in. Mention item {unit.id}.
            </div>
          </div>

          <div data-testid="unit-others-panel">
            <span className="chip-brutal bg-tpeco-pink border-tpeco-pink text-white">MORE OPTIONS</span>
            <h2 className="mt-3 font-display uppercase text-3xl sm:text-4xl leading-[0.95]">
              SAME {(fuelLabel[unit.fuel] || unit.fuel).toUpperCase()} {(systemLabel[unit.system] || unit.system).toUpperCase()} · {sizeChip(unit)} — OTHER BRANDS WE INSTALL
            </h2>
            {others.length === 0 ? (
              <div className="mt-5 card-brutal p-6" data-testid="unit-others-empty">
                <p className="font-semibold text-neutral-700">This is the only {sizeChip(unit)} {(fuelLabel[unit.fuel] || unit.fuel).toLowerCase()} {(systemLabel[unit.system] || unit.system).toLowerCase()} unit we carry — and it's a great one.</p>
                <Link to="/" className="btn-brutal mt-4 !text-lg" data-testid="unit-others-see-all">SEE ALL UNITS &amp; PRICES</Link>
              </div>
            ) : (
            <div className="mt-5 grid sm:grid-cols-2 gap-4 lg:max-h-[600px] lg:overflow-y-auto lg:pr-2" data-testid="unit-others-grid">
              {others.map((u) => (
                <Link
                  key={u.id}
                  to={`/units/${u.slug}`}
                  className="card-brutal flex flex-col hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#169ED9] transition-[transform,box-shadow] duration-150"
                  data-testid={`other-unit-${u.id}`}
                >
                  <div className="relative bg-white border-b-[3px] border-tpeco-ink flex items-center justify-center h-32 p-3">
                    <img src={`/unit-images/${u.slug}-1200x1200.jpg`} alt={`${u.brand} ${u.model} hot water system`} loading="lazy" className="max-h-full max-w-full object-contain" />
                    <span className="absolute top-2 left-2 chip-brutal bg-tpeco-blue border-tpeco-blue text-white !text-xs !px-2">{u.id}</span>
                  </div>
                  <div className="p-3 flex flex-col flex-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-neutral-500">{u.brand} · {u.fuel} {u.system}</span>
                    <h3 className="font-display uppercase text-lg leading-tight mt-0.5">{u.model}</h3>
                    <div className="mt-auto pt-2 flex items-end justify-between gap-2">
                      <div>
                        <span className="block text-[9px] font-extrabold uppercase tracking-widest text-neutral-500">INSTALLED</span>
                        <span className="font-display text-2xl leading-none text-tpeco-blue">${u.price.toLocaleString()}</span>
                      </div>
                      <span className="chip-brutal bg-tpeco-smoke !text-[10px] !px-2 whitespace-nowrap">{u.people}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            )}
          </div>
        </div>
      </main>

      <Reviews />
      <Faq />
      <Footer />
    </div>
  );
}
